import { useState, useCallback, useEffect } from 'react';
import type { EchoMission } from '@vtak/shared';
import { queryKeys } from '@vtak/shared';
import { apiClient } from '@/services/api-client';
import { queryClient } from '@/queries/query-client';
import { useMissionsStore } from '@/stores/missions-store';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { RefreshCw } from 'lucide-react';
import echoWordmark from '@/assets/echo-wordmark.png';

/** Status badge color mapping */
const STATUS_STYLES: Record<EchoMission['status'], string> = {
  STARTED: 'bg-green-500/20 text-green-400',
  ENDED: 'bg-zinc-500/20 text-zinc-400',
};

/** Echo missions list view with create form */
export function EchoList(): React.JSX.Element {
  const missions = useMissionsStore((s) => s.missions);
  const [newName, setNewName] = useState('');

  const fetchMissions = useCallback(async () => {
    try {
      const data = await apiClient.getEchoMissions();
      useMissionsStore.getState().setMissions(data);
    } catch {
      /* health check shows status */
    }
  }, []);

  useEffect(() => {
    fetchMissions();
  }, [fetchMissions]);

  async function handleCreate(): Promise<void> {
    const trimmed = newName.trim();
    if (!trimmed) return;
    try {
      const mission = await apiClient.createEchoMission(trimmed);
      if (mission) {
        setNewName('');
        await fetchMissions();
      }
    } catch {
      /* health check shows status */
    }
  }

  async function handleSelect(mission: EchoMission): Promise<void> {
    const { joinCode } = mission;
    if (!joinCode) return;
    try {
      const detail = await queryClient.fetchQuery({
        queryKey: queryKeys.tak.echoMissionDetail(joinCode),
        queryFn: () => apiClient.getEchoMissionDetail(joinCode),
      });
      if (detail) useMissionsStore.getState().setSelectedDetail(detail);
    } catch {
      /* silent */
    }
  }

  return (
    <div className="flex flex-1 flex-col gap-3 overflow-hidden p-4">
      <div className="flex items-center justify-between">
        <img src={echoWordmark} alt="Echo" className="h-5 opacity-70" />
        <Button size="sm" variant="outline" onClick={fetchMissions} className="gap-1.5">
          <RefreshCw className="h-3 w-3" />
          Refresh
        </Button>
      </div>

      <ScrollArea className="flex-1">
        <ul className="flex flex-col gap-1">
          {missions.length === 0 && (
            <li className="py-2 text-sm text-muted-foreground">No missions found</li>
          )}
          {missions.map((mission) => (
            <MissionItem key={mission.joinCode || mission.name} mission={mission} onSelect={handleSelect} />
          ))}
        </ul>
      </ScrollArea>

      <Separator />

      <div className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold">Create Mission</h3>
        <Input
          placeholder="Mission name"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleCreate();
          }}
        />
        <Button size="sm" onClick={handleCreate}>
          Create
        </Button>
      </div>
    </div>
  );
}

/** Single mission row — clickable to open detail */
function MissionItem({
  mission,
  onSelect,
}: {
  mission: EchoMission;
  onSelect: (m: EchoMission) => void;
}): React.JSX.Element {
  return (
    <li
      className="cursor-pointer rounded-md bg-white/[0.04] px-3 py-2 text-sm transition-colors hover:bg-white/[0.08]"
      onClick={() => onSelect(mission)}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold">{mission.name || 'Unnamed'}</span>
        <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${STATUS_STYLES[mission.status]}`}>
          {mission.status}
        </span>
      </div>
      {mission.creator && (
        <span className="mt-0.5 block text-[11px] text-muted-foreground">
          {mission.creator}
        </span>
      )}
    </li>
  );
}
